import type { List } from 'immutable'
import type { ReactElement } from 'react'
import { useRef, useState } from 'react'
import cc from 'classcat'

import { Popover } from '../../../../Popover'

interface CustomSelectProps {
  readonly selectedIndex?: number
  readonly defaultIndex?: number
  readonly options: List<any>
  readonly renderOption: (option: any) => ReactElement
  readonly showSelectedInList?: boolean
  readonly renderSelectedOption?: ((option: any) => ReactElement) | null
  readonly disabled?: boolean
  readonly onChange?: (index: number) => void
  readonly name?: string | null
  readonly className?: string
}

export default function CustomSelect({
  selectedIndex = 0,
  defaultIndex = 0,
  options,
  renderOption,
  showSelectedInList = false,
  renderSelectedOption = null,
  disabled = false,
  onChange = () => null,
  name = null,
  className,
}: CustomSelectProps): ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const ref = useRef<HTMLDivElement>(null)

  const handleClose = (): void => {
    setIsOpen(false)
  }

  const renderDropdownList = (): ReactElement => {
    return (
      <ul className="ep-custom-select-list">
        {(options.map(renderOption).toArray() as ReactElement[]).map((renderedElement, index) => {
          if ((showSelectedInList || index !== selectedIndex) && renderedElement) {
            return (
              <li
                key={index}
                className="ep-custom-select-list-item"
                role="option"
                aria-selected={selectedIndex === index}
                onClick={(e) => {
                  e.preventDefault()
                  onChange(index)
                  handleClose()
                }}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onChange(index)
                    handleClose()
                  }
                }}
              >
                <button className="ep-custom-select-option">{renderedElement}</button>
              </li>
            )
          }
          return null
        })}
      </ul>
    )
  }

  const showOptions = !disabled && isOpen
  const selectedValue = options.get(selectedIndex, options.get(defaultIndex))
  const renderSelected =
    renderSelectedOption && typeof renderSelectedOption === 'function' ? renderSelectedOption : renderOption

  const trigger = (
    <button type="button" className="ep-custom-select" disabled={disabled}>
      <div className="ep-custom-select-alignment">{renderSelected(selectedValue)}</div>
      <span className={cc({ open: isOpen })} />
    </button>
  )

  return (
    <div ref={ref} data-name={name} className={className}>
      <Popover
        trigger={trigger}
        open={isOpen}
        onOpenChange={(open) => setIsOpen(open)}
        side="bottom"
        align="start"
        className="ep-custom-select-options"
        sideOffset={0}
        hideArrow
      >
        {showOptions && renderDropdownList()}
      </Popover>
    </div>
  )
}
